import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "common/api/httpClient";
import { RootState } from "store";

export type Stage = {
  name: string;
  formDefinitionId: string;
};

type StagesCRUDState = {
  isLoading: boolean;
  error?: string;
  stages?: Stage[];
  stagesEdited?: Stage[];
  eTag?: string;
  hasStagesError: boolean;
  initialStageName: string | null;
};

const initialState: StagesCRUDState = {
  isLoading: false,
  hasStagesError: false,
  initialStageName: null,
};

const reorder = <T>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const editFormDefinitionStages = createAsyncThunk(
  "developer/formsCRUD/editStagesFormDefinition",
  async (formDefinitionId: string, { getState }) => {
    const { stagesEdited, eTag, initialStageName } = (getState() as RootState)
      .developer.stagesCRUD;

    const newStages: any = {
      stages: stagesEdited,
      eTag,
      initialStageName,
    };

    const response = await httpClient.put(
      `FormDefinition/${formDefinitionId}/stage`,
      newStages
    );

    return { eTag: response.data.eTag };
  }
);

type GetFormDefinitionStagesResponse = {
  eTag: string;
  stages: Stage[];
  initialStageName: string | null;
};

export const getFormDefinitionStages = createAsyncThunk(
  "developer/formsCRUD/getFormDefinitionsStages",
  async (formDefinitionId: string) => {
    const response = await httpClient.get<GetFormDefinitionStagesResponse>(
      `FormDefinition/${formDefinitionId}/stage`
    );

    return response.data;
  }
);

export const developerStagesCRUDSlice = createSlice({
  name: "developer/formsStagesCRUD",
  initialState,
  reducers: {
    handleChangeInitialStage: (state, action: PayloadAction<string>): void => {
      state.initialStageName = action.payload;
    },
    handleChangeFormDefinitionStage: (
      state,
      action: PayloadAction<{
        index: number;
        newStage: Partial<Stage>;
      }>
    ): void => {
      const { index, newStage } = action.payload;

      if (state.stagesEdited) {
        state.stagesEdited[index] = {
          ...state.stagesEdited[index],
          ...newStage,
        };
      }
    },
    addFormDefinitionStage: (state): void => {
      const newStage: Stage = {
        name: "",
        formDefinitionId: "",
      };

      (state.stagesEdited ?? []).push(newStage);
    },
    deleteFormDefinitionStage: (
      state,
      action: PayloadAction<{ index: number }>
    ): void => {
      const { index } = action.payload;
      const formDefinitionStages = state.stagesEdited;

      formDefinitionStages?.splice(index, 1);
    },
    resetStagesEdited: (state): void => {
      state.stagesEdited = state.stages;
    },
    reorderFormDefinitionStages: (
      state,
      action: PayloadAction<{
        sourceIndex: number;
        destinationIndex: number;
      }>
    ): void => {
      const { sourceIndex, destinationIndex } = action.payload;

      const newOrderedStages = reorder<Stage>(
        state.stagesEdited!,
        sourceIndex,
        destinationIndex
      );

      state.stagesEdited = newOrderedStages;
    },
    setStagesError: (state, action: PayloadAction<boolean>): void => {
      state.hasStagesError = action.payload;
    },
    resetStagesState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getFormDefinitionStages.pending, (state) => {
      state.isLoading = true;
      state.error = "";
    });
    builder.addCase(getFormDefinitionStages.fulfilled, (state, action) => {
      const { stages, eTag, initialStageName } = action.payload;

      state.isLoading = false;
      state.stages = stages;
      state.stagesEdited = stages;
      state.eTag = eTag;
      state.initialStageName = initialStageName;
    });
    builder.addCase(getFormDefinitionStages.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
    builder.addCase(editFormDefinitionStages.pending, (state) => {
      state.isLoading = true;
      state.error = "";
    });
    builder.addCase(editFormDefinitionStages.fulfilled, (state, action) => {
      const { eTag } = action.payload;

      state.isLoading = false;
      state.eTag = eTag;
    });
    builder.addCase(editFormDefinitionStages.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
      state.stages = undefined;
      state.stagesEdited = undefined;
    });
  },
});

export const {
  addFormDefinitionStage,
  handleChangeFormDefinitionStage,
  deleteFormDefinitionStage,
  resetStagesEdited,
  reorderFormDefinitionStages,
  setStagesError,
  resetStagesState,
  handleChangeInitialStage,
} = developerStagesCRUDSlice.actions;

export default developerStagesCRUDSlice.reducer;
